import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Paper,
  Grid,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Download,
  ArrowUpRightFromSquare,
  Terminal,
  Verified,
  Settings,
  ShieldCheck,
  CheckCircle2,
  Copy,
  ChevronDown,
  Wifi,
  Server,
  FileText,
  ArrowRight,
  Activity,
} from 'lucide-react';
import dockerIcon from "@/assets/docker-mark-white.svg"
import { QuestionMark, NavigateBefore } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { alpha } from "@mui/material/styles";

// ─── Fake version / download info ────────────────────────────────────────────
// const AGENT_VERSION = 'v1.0.0';
const AGENT_FILENAME = `agente-app-dono-linux-amd64`;
const AGENT_DOWNLOAD_URL = '#'; // replace with real URL

// ─── Code snippet component ───────────────────────────────────────────────────
function CodeBlock({ code, language = 'bash' }: { code: string, language?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#3D3A34',
        borderRadius: 2,
        p: 2.5,
        mt: 1.5,
        mb: 1,
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
        fontSize: '0.82rem',
        color: '#e2e8f0',
        overflowX: 'auto',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Chip
        label={language}
        size="small"
        sx={{
          position: 'absolute',
          top: 10,
          left: 12,
          bgcolor: 'rgba(255,255,255,0.07)',
          color: '#9B9690',
          fontSize: '0.65rem',
          height: 20,
          fontFamily: 'inherit',
        }}
      />
      <Tooltip title={copied ? 'Copiado!' : 'Copiar'} placement="top">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            position: 'absolute',
            top: 6,
            right: 8,
            color: '#9B9690',
            '&:hover': { color: '#e2e8f0', bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          <Copy size={14} />
        </IconButton>
      </Tooltip>
      <Box component="pre" sx={{ m: 0, mt: 2.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {code}
      </Box>
    </Box>
  );
}

// ─── Info Card ────────────────────────────────────────────────────────────────
function InfoCard({ icon: Icon, title, description, color }: { icon: any, title: string, description: string, color: string }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        background: (theme) => alpha(theme.palette.background.paper, 0.75),
        backdropFilter: 'blur(10px)',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
        transition: 'all 0.25s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 8px 28px rgba(0,0,0,0.08)`,
          borderColor: color,
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 2.5,
            mb: 2,
            bgcolor: `${color}18`,
            color,
          }}
        >
          <Icon size={22} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ConnectionAgentPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const featureCards = [
    {
      icon: ShieldCheck,
      title: 'Comunicação Segura',
      description: 'Todo tráfego é criptografado via certificados.',
      color: theme.palette.success.main,
    },
    {
      icon: Activity,
      title: 'Fácil de Instalar',
      description: 'Instalador passo-a-passo para fácil configuração.',
      color: theme.palette.primary.main,
    },
    {
      icon: Server,
      title: 'Conexão controlada',
      description: 'Comunicação entre servidores de forma controlada e facilitada.',
      color: theme.palette.warning.main,
    },
  ];

  const installSteps = [
    {
      label: 'Baixar o agente',
      icon: <Download size={18} />,
      content: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Faça o download do agente para o servidor de destino (atualmente apenas Linux).
          </Typography>
          <Button
            variant="contained"
            startIcon={<Download size={16} />}
            href={AGENT_DOWNLOAD_URL}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, mb: 2 }}
          >
            Baixar {AGENT_FILENAME}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', color: 'grey.500', mb: 1 }}>
            Ou via terminal:
          </Typography>
          <CodeBlock
            code={`wget https://downloads.example.com/agent/${AGENT_FILENAME}\ntar -xzf ${AGENT_FILENAME}\ncd connection-agent`}
          />
        </>
      ),
    },
    {
      label: 'Instale o Docker',
      icon: <img src={dockerIcon} width={18} height={18} />,
      content: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Caso ainda não possua no servidor, instale o <strong>Docker</strong> para que o agente possa criar os containers.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowUpRightFromSquare size={16} />}
            href="https://www.docker.com/"
            target='_blank'
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, mb: 2 }}
          >
            Baixar o Docker
          </Button>
          <Typography variant="caption" sx={{ display: 'block', color: 'grey.500', mb: 1 }}>
            Ou via terminal:
          </Typography>
          <CodeBlock
            code={`#para Ubuntu
sudo apt-get update
sudo apt install ./docker-desktop-amd64.deb

#para Debian
sudo apt-get update
sudo apt-get install ./docker-desktop-amd64.deb

#para Arch
sudo pacman -S docker
#ou
sudo yay -S docker`}
          />
        </>
      ),
    },
    {
      label: 'Executar o Instalador',
      icon: <Terminal size={18} />,
      content: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Encontre o diretório no qual foi realizado o download do instalador e execute usando{' '}
            <strong>./go-file</strong>
          </Typography>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Importante para a instalação</AlertTitle>
            Caso não exista porta pública no servidor de instalação, o instalador irá utilizar um serviço secundário
            para conseguir se comunicar com o aplicativo. Neste caso,{' '}
            <strong>é necessário requisitar as credenciais da VPN com o time de suporte do App do Dono</strong> para
            finalizar a instalação corretamente
          </Alert>
        </>
      ),
    },
    {
      label: 'Definir permissões de acesso',
      icon: <Settings size={18} />,
      content: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Configure quais recursos internos o agente poderá expor. Por padrão, apenas leitura de métricas do sistema
            está habilitada.
          </Typography>
          <CodeBlock
            language="yaml"
            code={`# Adicione ao config.yaml
permissions:
  system_metrics: true    # CPU, memória, disco
  database_status: false  # Requer configuração adicional
  log_streaming: false    # Envio de logs em tempo real
  custom_endpoints: []    # URLs internas expostas ao painel`}
          />
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              Atenção
            </AlertTitle>
            Habilite apenas as permissões necessárias para o seu caso de uso. Cada permissão adicional amplia a superfície
            de acesso.
          </Alert>
        </>
      ),
    },
    {
      label: 'Realizar a Configuração',
      icon: <Verified size={18} />,
      content: (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Com o instalador iniciado no terminal, siga os passos abaixo para configurar o agente:
          </Typography>

          {/* Passo 1: Docker */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>
            1. Autenticação do Repositório
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Informe as credenciais de acesso ao repositório Docker. Por padrão, utilize:
          </Typography>
          <CodeBlock
            language="bash"
            code={`# Dados a informar
user: davinti
password: davinti`}
          />

          {/* Passo 2: Rede / vproxy */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>
            2. Conectividade e IP
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Responda se o servidor possui <strong>IP Público</strong>. Caso não possua, o instalador solicitará informações para configuração de uma <strong>VPN (Wireguard)</strong>
          </Typography>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              Importante!
            </AlertTitle>
            No momento, <strong>não temos um serviço para gerar estes dados</strong>. Portanto, é necessário entrar em contato com o nosso suporte para que sejam geradas estas credenciais
          </Alert>
          <Typography variant="body2" component="div" sx={{ color: 'text.secondary', ml: 2, mb: 1 }}>
            • Chave Privada, IP Virtual e PSK (Pre-Shared Key).<br />
            • Proxy EDPS (Ex: 22:127.0.0.1:22).<br />
            • MTU (Padrão 1380) e Protocolo (UDP/TCP).
          </Typography>

          {/* Passo 3: Servidor e Ambiente */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>
            3. Configurações do Servidor
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Defina a porta de escuta (ex: 8080) do container, o tempo de timeout e o ambiente (development ou production).
          </Typography>

          {/* Passo 4: Banco de Dados */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>
            4. Banco de Dados
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Selecione o driver (<strong>Postgres</strong> ou <strong>Oracle</strong>) e insira a URL de conexão:
          </Typography>
          <CodeBlock
            language="bash"
            code={`#exemplo de padrão de URL postgres
postgres://usuario:senha@host:5432/app_dono_db`}
          />

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Configure também o limite de conexões ativas (Mínimo/Máximo).
          </Typography>

          {/* Passo 5: Certificados */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>
            5. Certificados Digitais
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Solicite os certificados para o nosso time de suporte e indique o caminho do diretório e os nomes exatos dos arquivos (.crt, .key e .ca) para a comunicação segura.
            Este diretório será mapeado para dentro do container, e os nomes dos certificados serão utilizados para buscar os arquivos dentro deste.{' '}
            <strong>Apagar o diretório após instalação acarretará no container perdendo referência aos arquivos</strong>
          </Typography>

          <Alert severity="success" sx={{ mt: 3, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              Sucesso!
            </AlertTitle>
            Caso o instalador encerre sem erros, <strong>o agente está configurado e rodando</strong> no servidor.
          </Alert>
        </>
      ),
    },
  ];

  const faqItems = [
    {
      question: 'O agente precisa de acesso à internet no servidor do cliente?',
      answer:
        'Sim. O agente faz uma conexão para o nosso endpoint, portanto falta de internet no servidor acarreta em problemas de comunicação com a nossa aplicação.',
    },
    {
      question: 'Quais sistemas operacionais são suportados?',
      answer:
        'No momento, apenas servidores Linux (amd64, arm64) são suportados pelo instalador.',
    },
    {
      question: 'Como o agente autentica com o servidor?',
      answer:
        'Através de certificados configurados no momento da instalação do Agente',
    },
    {
      question: 'O que acontece se a conexão cair?',
      answer:
        'Assim que a conexão é restabelecida, o agente volta a se comunicar com o servidor.',
    },
    {
      question: 'O que acontece se o servidor for desligado?',
      answer:
        'Assim que for ligado novamente, os containeres Docker vão tentar reiniciar automaticamente. Caso não volte, pode ser iniciado manualmente ou contatado o suporte para verificação.',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh', bgcolor: 'background.default' }}>

      {/* ── Header ── */}
      <Box
        sx={{
          mb: 5,
          p: 3,
          borderRadius: 4,
          background: (theme) => alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              borderRadius: 2,
              flexShrink: 0,
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15) },
            }}
          >
            <NavigateBefore />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 52,
              height: 52,
              borderRadius: 3,
              bgcolor: `${theme.palette.primary.main}15`,
              color: theme.palette.primary.main,
            }}
          >
            <Wifi size={26} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em' }}>
              Agente de Conexão
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              Bridge segura entre o servidor do cliente e nossa plataforma.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {/* <Chip
            icon={<CheckCircle2 size={16} />}
            label={`Versão atual: ${AGENT_VERSION}`}
            size="small"
            sx={{
              borderRadius: 2.5,
              bgcolor: `${theme.palette.success.main}15`,
              color: theme.palette.success.dark,
              fontWeight: 700,
              px: 2.5,
              py: 2.5,
              border: `1px solid ${theme.palette.success.light}`,
            }}
          /> */}
          <Button
            variant="contained"
            startIcon={<Download size={16} />}
            href={AGENT_DOWNLOAD_URL}
            sx={{
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              px: 2.5,
              py: 1,
              boxShadow: 'none',
              '&:hover': { boxShadow: theme.shadows[3] },
            }}
          >
            Baixar Agente
          </Button>
        </Box>
      </Box>

      {/* ── Feature Cards ── */}
      <Grid container spacing={2.5} sx={{ mb: 5 }}>
        {featureCards.map((card) => (
          <Grid size={{ xs: 12, sm: 4 }} key={card.title}>
            <InfoCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* ── How it works ── */}
      <Card
        elevation={0}
        sx={{
          mb: 5,
          borderRadius: 4,
          background: (theme) => alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <FileText size={18} color={theme.palette.primary.main} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Como funciona
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 2 },
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {[
              { label: 'Servidor do Cliente', sub: 'onde o agente roda', color: theme.palette.primary.main },
              { label: 'Agente de Conexão', sub: 'processo Go em container', color: theme.palette.warning.main },
              { label: 'Nosso Servidor', sub: 'endpoint seguro', color: theme.palette.success.main },
            ].map((node, i, arr) => (
              <React.Fragment key={node.label}>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 3,
                    border: `2px solid ${node.color}30`,
                    bgcolor: `${node.color}08`,
                    textAlign: 'center',
                    minWidth: 140,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {node.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'grey.500' }}>
                    {node.sub}
                  </Typography>
                </Paper>
                {i < arr.length - 1 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    <ArrowRight size={18} color={theme.palette.grey[500]} />
                    <Typography variant="caption" sx={{ color: 'grey.500', fontSize: '0.65rem' }}>
                      mTLS
                    </Typography>
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
            O agente é instalado diretamente no servidor do cliente e configura todas as aplicações <strong>containerizadas</strong> necessárias
            para o funcionamento do <strong>App do Dono</strong>. A partir dos containers que rodamos, conseguimos fazer toda a
            comunicação de forma <strong>auditável e controlada</strong>, com criptografia baseada em certificados para conexão para uma conexão
            segura.
          </Typography>
        </CardContent>
      </Card>

      {/* ── Installation Guide ── */}
      <Card
        elevation={0}
        sx={{
          mb: 5,
          borderRadius: 4,
          background: (theme) => alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Terminal size={18} color={theme.palette.primary.main} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Guia de Instalação
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
            {installSteps.map((step, index) => (
              <Step key={step.label} completed={activeStep > index}>
                <StepLabel
                  onClick={() => setActiveStep(index)}
                  sx={{ cursor: 'pointer', '& .MuiStepLabel-label': { fontWeight: 700, color: 'text.primary' } }}
                  icon={
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor:
                          activeStep === index
                            ? theme.palette.primary.main
                            : activeStep > index
                              ? theme.palette.success.main
                              : alpha(theme.palette.text.primary, 0.12),
                        color:
                          activeStep === index
                            ? theme.palette.primary.contrastText
                            : activeStep > index
                              ? theme.palette.success.contrastText
                              : theme.palette.grey[500],
                        transition: 'all 0.2s',
                      }}
                    >
                      {activeStep > index ? <CheckCircle2 size={16} /> : step.icon}
                    </Box>
                  }
                >
                  {step.label}
                </StepLabel>
                <StepContent>
                  <Box sx={{ pt: 1, pb: 2 }}>
                    {step.content}
                    <Box sx={{ mt: 2.5, display: 'flex', gap: 1 }}>
                      {index < installSteps.length - 1 ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setActiveStep(index + 1)}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
                        >
                          Próximo passo
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          color="success"
                          startIcon={<CheckCircle2 size={14} />}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
                        >
                          Concluído!
                        </Button>
                      )}
                      {index > 0 && (
                        <Button
                          size="small"
                          onClick={() => setActiveStep(index - 1)}
                          sx={{ borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}
                        >
                          Voltar
                        </Button>
                      )}
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* ── FAQ ── */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: 4,
          background: (theme) => alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <QuestionMark sx={{ color: theme.palette.warning.main, width: 18, height: 18 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Dúvidas Frequentes
          </Typography>
        </Box>
        <CardContent sx={{ p: 1.5 }}>
          {faqItems.map((item, i) => (
            <Accordion
              key={i}
              elevation={0}
              disableGutters
              sx={{
                border: 'none',
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-root': { borderRadius: 2 },
              }}
            >
              <AccordionSummary expandIcon={<ChevronDown size={16} />}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      {/* ── Download CTA ── */}
      <Box
        sx={{
          p: 4,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, ${theme.palette.primary.light}08 100%)`,
          border: `1px solid ${theme.palette.primary.main}25`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Pronto para instalar?
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Baixe o agente e siga o guia acima. Em poucos minutos sua integração estará ativa.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<Download size={18} />}
          href={AGENT_DOWNLOAD_URL}
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 800,
            px: 4,
            py: 1.5,
            boxShadow: `0 4px 14px ${theme.palette.primary.main}40`,
            '&:hover': { boxShadow: `0 6px 20px ${theme.palette.primary.main}55` },
          }}
        >
          Baixar {AGENT_FILENAME}
        </Button>
      </Box>
    </Box>
  );
}